import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { MailchimpSettingsSchema, paths, type MailchimpSettings } from "@clevis/shared-types";
import { Button, Card, CardTitle, ConfirmButton, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { useCustomerTypes } from "../../hooks/useCustomerTypes";
import { useEntitlements } from "../../hooks/useEntitlements";
import { PlanLocked } from "../../components/PlanLocked";

// Setup → Mailchimp: the company's own Mailchimp account. Paste an API key,
// pick the audience, tick the customer types that go in, and new or changed
// customers join on their own every 10 minutes. Nothing is ever removed, and
// anyone who unsubscribed in Mailchimp stays unsubscribed.

interface Config {
  enabled: boolean;
  summary: string;
  connectedAt: number | null;
  s: MailchimpSettings;
}
interface Audience {
  id: string;
  name: string;
  members: number;
}
interface Draft {
  audienceId: string;
  statusIfNew: "pending" | "subscribed";
  types: string[];
  tags: string;
}
interface SyncResult {
  scanned: number;
  added: number;
  updated: number;
  skipped: number;
  failed: number;
  message: string;
}

const when = (ms: number) => new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const runLine = (r: SyncResult) =>
  `${r.scanned} looked at, ${r.added} added, ${r.updated} updated, ${r.skipped} left out${r.failed ? `, ${r.failed} failed` : ""}${r.message ? ` · ${r.message}` : ""}`;

export function MailchimpPage() {
  const { tenantId } = useAuth();
  const on = useEntitlements().hasFeature("emailMarketing");
  const companyTypes = useCustomerTypes(tenantId);
  const [cfg, setCfg] = useState<Config | null | undefined>(undefined);
  const [key, setKey] = useState("");
  const [audiences, setAudiences] = useState<Audience[] | null>(null);
  const [account, setAccount] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [synced, setSynced] = useState<SyncResult | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(
      doc(db, paths.tenantIntegrationConfig(tenantId, "mailchimp")),
      (snap) => {
        const d = snap.data();
        setCfg(
          d
            ? { enabled: d.enabled === true, summary: String((d.credentialsSummary as Record<string, string> | undefined)?.apiKey ?? ""), connectedAt: (d.connectedAt as number | null) ?? null, s: MailchimpSettingsSchema.parse(d.mailchimp ?? {}) }
            : null,
        );
      },
      () => setCfg(null),
    );
  }, [tenantId]);

  const connected = !!cfg?.enabled;
  const s = cfg?.s ?? null;
  // The form follows what's saved until someone edits it.
  useEffect(() => {
    if (s && !draft) setDraft({ audienceId: s.audienceId, statusIfNew: s.statusIfNew, types: s.types, tags: s.tags.join(", ") });
  }, [s, draft]);
  useEffect(() => {
    if (!connected || !tenantId || audiences) return;
    void call<{ account: string; audiences: Audience[] }>("listMailchimpAudiences", { tenantId }, { mutation: false })
      .then((r) => {
        setAudiences(r.audiences);
        setAccount(r.account);
      })
      .catch((e) => setNote((e as Error).message));
  }, [connected, tenantId, audiences]);

  async function act(fn: () => Promise<unknown>, done?: string) {
    setBusy(true);
    setNote("");
    try {
      await fn();
      if (done) setNote(done);
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!tenantId || cfg === undefined) return <p className="text-sm text-steel">Loading…</p>;

  const typeChoices = [...new Set([...companyTypes, ...(draft?.types ?? [])])];
  const dirty = !!s && !!draft && (draft.audienceId !== s.audienceId || draft.statusIfNew !== s.statusIfNew || draft.types.join("|") !== s.types.join("|") || draft.tags !== s.tags.join(", "));

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-lg font-bold">Mailchimp</h1>
        <p className="text-xs text-steel">Your own Mailchimp account. New and changed customers of the types you pick join your audience on their own, every 10 minutes.</p>
      </div>
      {!on ? <PlanLocked feature="emailMarketing" page /> : null}
      {note ? <p className="rounded-lg bg-cream px-3 py-1.5 text-xs text-ink">{note}</p> : null}

      <Card className="space-y-2">
        <CardTitle>Account</CardTitle>
        {connected ? (
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-ink">
              Connected{account || s?.accountName ? ` to ${account || s?.accountName}` : ""} · key {cfg?.summary || "••••"}
              {cfg?.connectedAt ? <span className="text-steel"> · since {when(cfg.connectedAt)}</span> : null}
            </span>
            <ConfirmButton
              confirm="Disconnect Mailchimp? Nothing is removed from your audience; Clevis just stops adding people."
              confirmLabel="Disconnect"
              busy={busy}
              className="px-2 py-1 text-xs"
              onConfirm={() =>
                void act(async () => {
                  await call("disconnectMailchimp", { tenantId });
                  setAudiences(null);
                  setDraft(null);
                }, "Disconnected.")
              }
            >
              Disconnect
            </ConfirmButton>
          </div>
        ) : (
          <>
            <p className="text-sm text-ink/80">
              In Mailchimp, open your profile → <span className="font-semibold">Extras → API keys → Create a key</span>, copy the whole key and paste it here. Clevis checks it, lists your audiences, and keeps the key
              encrypted; nobody sees it again.
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[16rem] flex-1">
                <Label>Mailchimp API key</Label>
                <Input type="password" autoComplete="off" value={key} placeholder="…-us21" onChange={(e) => setKey(e.target.value)} disabled={!on} />
              </div>
              <Button
                type="button"
                busy={busy}
                disabled={!on || key.trim().length < 20}
                onClick={() =>
                  void act(async () => {
                    const r = await call<{ account: string; audiences: Audience[]; audienceId: string }>("connectMailchimp", { tenantId, apiKey: key.trim() });
                    setKey("");
                    setAudiences(r.audiences);
                    setAccount(r.account);
                    setDraft(null);
                    setNote(r.audiences.length ? `Connected to ${r.account || "Mailchimp"}. ${r.audiences.length === 1 ? "Your audience is picked below." : "Pick the audience below."}` : `Connected to ${r.account || "Mailchimp"}, but it has no audience yet. Make one in Mailchimp first.`);
                  })
                }
              >
                Connect
              </Button>
            </div>
          </>
        )}
      </Card>

      {connected && draft && s ? (
        <>
          <Card className="space-y-3">
            <CardTitle>Who goes in</CardTitle>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Audience</Label>
                <Select value={draft.audienceId} onChange={(e) => setDraft({ ...draft, audienceId: e.target.value })}>
                  <option value="">Pick an audience…</option>
                  {(audiences ?? (s.audienceId ? [{ id: s.audienceId, name: s.audienceName, members: 0 }] : [])).map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                      {a.members ? ` (${a.members.toLocaleString()})` : ""}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label>New people are added as</Label>
                <Select value={draft.statusIfNew} onChange={(e) => setDraft({ ...draft, statusIfNew: e.target.value as Draft["statusIfNew"] })}>
                  <option value="pending">Pending: Mailchimp emails them to confirm (recommended)</option>
                  <option value="subscribed">Subscribed right away</option>
                </Select>
              </div>
            </div>
            <div>
              <Label>Customer types that go in</Label>
              <div className="flex flex-wrap gap-1.5">
                {typeChoices.map((t) => {
                  const checked = draft.types.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      className={cn("rounded-full border px-2.5 py-1 text-xs transition-colors", checked ? "border-ink bg-ink text-white" : "border-steel/30 bg-white text-ink hover:border-ink")}
                      onClick={() => setDraft({ ...draft, types: checked ? draft.types.filter((x) => x !== t) : [...draft.types, t] })}
                    >
                      {checked ? "✓ " : "+ "}
                      {t}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-[11px] text-steel">A customer with no type set counts as a builder when they own a project, a business when they have a company name, else a homeowner. Edit the list under Customers → "Customer types…".</p>
            </div>
            <div>
              <Label>Tags for everyone Clevis adds (optional, comma-separated)</Label>
              <Input value={draft.tags} placeholder="Clevis, Service customers" onChange={(e) => setDraft({ ...draft, tags: e.target.value })} />
              <p className="mt-1 text-[11px] text-steel">Everyone is also tagged with their customer type and "Lead source: …" when one is known, so you can build segments.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                busy={busy}
                disabled={!dirty || !draft.audienceId}
                onClick={() =>
                  void act(async () => {
                    await call("setMailchimpSettings", {
                      tenantId,
                      audienceId: draft.audienceId,
                      statusIfNew: draft.statusIfNew,
                      types: draft.types,
                      tags: draft.tags.split(",").map((t) => t.trim()).filter(Boolean),
                    });
                    setDraft(null);
                  }, "Saved. The next run picks up everyone now included.")
                }
              >
                Save
              </Button>
              {!draft.audienceId ? <span className="text-xs text-steel">Pick an audience to save.</span> : null}
            </div>
          </Card>

          <Card className="space-y-2">
            <CardTitle>Syncing</CardTitle>
            <p className="text-sm text-ink/80">
              Every 10 minutes Clevis adds or updates customers changed since the last run. Nothing is ever removed, and anyone who unsubscribed in Mailchimp stays unsubscribed. Customers without an email, archived
              ones and other types are left out.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                busy={busy}
                disabled={!s.audienceId}
                onClick={() =>
                  void act(async () => {
                    const r = await call<SyncResult>("syncMailchimpNow", { tenantId, everyone: true }, { timeoutMs: 540_000 });
                    setSynced(r);
                  })
                }
              >
                Sync everyone now
              </Button>
              {synced ? <span className="text-xs text-ink">{runLine(synced)}</span> : null}
            </div>
            {s.lastRun ? (
              <p className="text-xs text-steel">
                Last run {when(s.lastRun.at)}: {runLine(s.lastRun)}
              </p>
            ) : (
              <p className="text-xs text-steel">No run yet.</p>
            )}
          </Card>

          <Card>
            <CardTitle>What Mailchimp gets</CardTitle>
            <ul className="list-disc space-y-1 pl-5 text-xs text-ink/80">
              <li>Email, first and last name, phone, and the address when it's complete.</li>
              <li>Tags: the customer type, "Lead source: …" when known, and your tags above.</li>
              <li>Each customer's page shows when they went in; the Customers list can filter by type.</li>
              <li>Two companies, two connections: each links its own Mailchimp account and audience.</li>
            </ul>
          </Card>
        </>
      ) : null}
    </div>
  );
}
